import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Calendar, Users, CreditCard, Shield, Check, AlertCircle, Lock, Mail, Phone, User, MapPin, BedDouble, Clock } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
interface BookingData {
  hotelId: string;
  hotelName: string;
  hotelImage: string;
  hotelLocation: string;
  roomId: string;
  roomName: string;
  roomPrice: number;
  checkIn: string;
  checkOut: string;
  guests: number;
}
// Sample booking data - in real app, this would come from URL params or state
const sampleBookingData: BookingData = {
  hotelId: 'd1',
  hotelName: 'Burj Al Arab Jumeirah',
  hotelImage: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80',
  hotelLocation: 'Dubai, UAE',
  roomId: 'r1',
  roomName: 'Deluxe One Bedroom Suite',
  roomPrice: 1850,
  checkIn: '',
  checkOut: '',
  guests: 2
};
interface BookingPageProps {
  bookingData?: BookingData;
}
export function BookingPage({
  bookingData = sampleBookingData
}: BookingPageProps) {
  const [step, setStep] = useState<'details' | 'payment' | 'confirmation'>('details');
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [checkIn, setCheckIn] = useState(bookingData.checkIn);
  const [checkOut, setCheckOut] = useState(bookingData.checkOut);
  const [guests, setGuests] = useState(bookingData.guests);
  // Payment state
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  // Calculate pricing
  const calculateNights = () => {
    if (!checkIn || !checkOut) return 1;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const nights = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 1;
  };
  const nights = calculateNights();
  const subtotal = bookingData.roomPrice * nights;
  const taxesAndFees = subtotal * 0.15; // 15% taxes and fees
  const total = subtotal + taxesAndFees;
  const handleContinueToPayment = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('payment');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleConfirmBooking = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('confirmation');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-270">
      <Header data-id="element-271" />

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200" data-id="element-272">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-273">
          <div className="flex items-center justify-between max-w-2xl mx-auto" data-id="element-274">
            {[{
            id: 'details',
            label: 'Guest Details',
            icon: User
          }, {
            id: 'payment',
            label: 'Payment',
            icon: CreditCard
          }, {
            id: 'confirmation',
            label: 'Confirmation',
            icon: Check
          }].map((item, index) => <div key={item.id} className="flex items-center" data-id="element-275">
                <div className="flex flex-col items-center" data-id="element-276">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${step === item.id ? 'bg-travel-blue-600 text-white' : index < ['details', 'payment', 'confirmation'].indexOf(step) ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`} data-id="element-277">
                    <item.icon className="w-5 h-5" data-id="element-278" />
                  </div>
                  <span className="text-xs mt-2 font-medium text-gray-600" data-id="element-279">
                    {item.label}
                  </span>
                </div>
                {index < 2 && <div className={`w-24 h-0.5 mx-2 transition-colors ${index < ['details', 'payment', 'confirmation'].indexOf(step) ? 'bg-green-500' : 'bg-gray-200'}`} data-id="element-280" />}
              </div>)}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-281">
        <div className="mb-6" data-id="element-282">
          <a href="#" onClick={e => {
          e.preventDefault();
          window.history.back();
        }} className="text-travel-blue-600 hover:text-travel-blue-700 flex items-center gap-1 text-sm font-medium" data-id="element-283">
            <ChevronLeft className="w-4 h-4" data-id="element-284" />
            Back to Hotel
          </a>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-285">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6" data-id="element-286">
            {step === 'details' && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-287">
                <form onSubmit={handleContinueToPayment} className="space-y-6" data-id="element-288">
                  {/* Booking Details */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-id="element-289">
                    <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-290">
                      Booking Details
                    </h2>

                    <div className="grid sm:grid-cols-2 gap-4" data-id="element-291">
                      <div data-id="element-292">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-293">
                          Check-in Date *
                        </label>
                        <div className="relative" data-id="element-294">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-295" />
                          <input type="date" required value={checkIn} onChange={e => setCheckIn(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none" data-id="element-296" />
                        </div>
                      </div>

                      <div data-id="element-297">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-298">
                          Check-out Date *
                        </label>
                        <div className="relative" data-id="element-299">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-300" />
                          <input type="date" required value={checkOut} onChange={e => setCheckOut(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none" data-id="element-301" />
                        </div>
                      </div>

                      <div data-id="element-302">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-303">
                          Number of Guests *
                        </label>
                        <div className="relative" data-id="element-304">
                          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-305" />
                          <select required value={guests} onChange={e => setGuests(Number(e.target.value))} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none appearance-none cursor-pointer" data-id="element-306">
                            {[1, 2, 3, 4, 5, 6].map(num => <option key={num} value={num} data-id="element-307">
                                {num} {num === 1 ? 'Guest' : 'Guests'}
                              </option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Guest Information */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-id="element-308">
                    <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-309">
                      Guest Information
                    </h2>

                    <div className="space-y-4" data-id="element-310">
                      <div className="grid sm:grid-cols-2 gap-4" data-id="element-311">
                        <div data-id="element-312">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-313">
                            First Name *
                          </label>
                          <div className="relative" data-id="element-314">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-315" />
                            <input type="text" required value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="John" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none" data-id="element-316" />
                          </div>
                        </div>

                        <div data-id="element-317">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-318">
                            Last Name *
                          </label>
                          <div className="relative" data-id="element-319">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-320" />
                            <input type="text" required value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Doe" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none" data-id="element-321" />
                          </div>
                        </div>
                      </div>

                      <div data-id="element-322">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-323">
                          Email Address *
                        </label>
                        <div className="relative" data-id="element-324">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-325" />
                          <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="john.doe@example.com" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none" data-id="element-326" />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4" data-id="element-327">
                        <div data-id="element-328">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-329">
                            Phone Number *
                          </label>
                          <div className="relative" data-id="element-330">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-331" />
                            <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 123-4567" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none" data-id="element-332" />
                          </div>
                        </div>

                        <div data-id="element-333">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-334">
                            Country *
                          </label>
                          <div className="relative" data-id="element-335">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-336" />
                            <select required value={country} onChange={e => setCountry(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none appearance-none cursor-pointer" data-id="element-337">
                              <option value="" data-id="element-338">Select Country</option>
                              <option value="US" data-id="element-339">United States</option>
                              <option value="UK" data-id="element-340">United Kingdom</option>
                              <option value="CA" data-id="element-341">Canada</option>
                              <option value="AU" data-id="element-342">Australia</option>
                              <option value="AE" data-id="element-343">United Arab Emirates</option>
                              <option value="IN" data-id="element-344">India</option>
                              <option value="SG" data-id="element-345">Singapore</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-346">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-347">
                          Special Requests (Optional)
                        </label>
                        <textarea value={specialRequests} onChange={e => setSpecialRequests(e.target.value)} rows={4} placeholder="Any special requests or requirements..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none resize-none" data-id="element-348" />
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="w-full py-4 bg-travel-blue-600 text-white font-semibold rounded-xl hover:bg-travel-blue-700 transition-colors shadow-lg shadow-travel-blue-600/20" data-id="element-349">
                    Continue to Payment
                  </button>
                </form>
              </motion.div>}

            {step === 'payment' && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-350">
                <form onSubmit={handleConfirmBooking} className="space-y-6" data-id="element-351">
                  {/* Payment Method */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-id="element-352">
                    <div className="flex items-center gap-2 mb-6" data-id="element-353">
                      <Lock className="w-5 h-5 text-green-600" data-id="element-354" />
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-355">
                        Secure Payment
                      </h2>
                    </div>

                    <div className="space-y-4" data-id="element-356">
                      <div data-id="element-357">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-358">
                          Card Number *
                        </label>
                        <div className="relative" data-id="element-359">
                          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-360" />
                          <input type="text" required value={cardNumber} onChange={e => setCardNumber(e.target.value)} placeholder="1234 5678 9012 3456" maxLength={19} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none" data-id="element-361" />
                        </div>
                      </div>

                      <div data-id="element-362">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-363">
                          Cardholder Name *
                        </label>
                        <input type="text" required value={cardName} onChange={e => setCardName(e.target.value)} placeholder="John Doe" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none" data-id="element-364" />
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-365">
                        <div data-id="element-366">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-367">
                            Expiry Date *
                          </label>
                          <input type="text" required value={expiryDate} onChange={e => setExpiryDate(e.target.value)} placeholder="MM/YY" maxLength={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none" data-id="element-368" />
                        </div>

                        <div data-id="element-369">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-370">
                            CVV *
                          </label>
                          <input type="text" required value={cvv} onChange={e => setCvv(e.target.value)} placeholder="123" maxLength={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-travel-blue-500 focus:border-transparent outline-none" data-id="element-371" />
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-blue-50 rounded-lg flex items-start gap-3" data-id="element-372">
                      <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-373" />
                      <div className="text-sm text-blue-900" data-id="element-374">
                        <p className="font-semibold mb-1" data-id="element-375">
                          Your payment is secure
                        </p>
                        <p className="text-blue-700" data-id="element-376">
                          We use industry-standard encryption to protect your
                          payment information.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Terms and Conditions */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-id="element-377">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-378">
                      <input type="checkbox" required checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} className="mt-1 w-4 h-4 text-travel-blue-600 border-gray-300 rounded focus:ring-travel-blue-500" data-id="element-379" />
                      <span className="text-sm text-gray-600" data-id="element-380">
                        I agree to the{' '}
                        <a href="#" className="text-travel-blue-600 hover:underline" data-id="element-381">
                          Terms and Conditions
                        </a>
                        ,{' '}
                        <a href="#" className="text-travel-blue-600 hover:underline" data-id="element-382">
                          Privacy Policy
                        </a>
                        , and{' '}
                        <a href="#" className="text-travel-blue-600 hover:underline" data-id="element-383">
                          Cancellation Policy
                        </a>
                      </span>
                    </label>
                  </div>

                  <div className="flex gap-4" data-id="element-384">
                    <button type="button" onClick={() => setStep('details')} className="flex-1 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors" data-id="element-385">
                      Back
                    </button>
                    <button type="submit" disabled={!agreeTerms} className="flex-1 py-4 bg-travel-blue-600 text-white font-semibold rounded-xl hover:bg-travel-blue-700 transition-colors shadow-lg shadow-travel-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-386">
                      Confirm Booking
                    </button>
                  </div>
                </form>
              </motion.div>}

            {step === 'confirmation' && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center" data-id="element-387">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-388">
                  <Check className="w-10 h-10 text-green-600" data-id="element-389" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-390">
                  Booking Confirmed!
                </h2>
                <p className="text-gray-600 mb-8 max-w-md mx-auto" data-id="element-391">
                  Your reservation has been confirmed. A confirmation email has
                  been sent to <span className="font-semibold" data-id="element-392">{email}</span>
                </p>

                <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left" data-id="element-393">
                  <h3 className="font-bold text-gray-900 mb-4" data-id="element-394">
                    Booking Details
                  </h3>
                  <div className="space-y-3 text-sm" data-id="element-395">
                    <div className="flex justify-between" data-id="element-396">
                      <span className="text-gray-600" data-id="element-397">
                        Confirmation Number:
                      </span>
                      <span className="font-semibold text-gray-900" data-id="element-398">
                        TS-{Date.now().toString().slice(-8)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-399">
                      <span className="text-gray-600" data-id="element-400">Hotel:</span>
                      <span className="font-semibold text-gray-900" data-id="element-401">
                        {bookingData.hotelName}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-402">
                      <span className="text-gray-600" data-id="element-403">Room:</span>
                      <span className="font-semibold text-gray-900" data-id="element-404">
                        {bookingData.roomName}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-405">
                      <span className="text-gray-600" data-id="element-406">Check-in:</span>
                      <span className="font-semibold text-gray-900" data-id="element-407">
                        {checkIn || 'Not set'}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-408">
                      <span className="text-gray-600" data-id="element-409">Check-out:</span>
                      <span className="font-semibold text-gray-900" data-id="element-410">
                        {checkOut || 'Not set'}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-411">
                      <span className="text-gray-600" data-id="element-412">Guests:</span>
                      <span className="font-semibold text-gray-900" data-id="element-413">
                        {guests}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4" data-id="element-414">
                  <button onClick={() => window.print()} className="flex-1 py-3 border-2 border-travel-blue-600 text-travel-blue-600 font-semibold rounded-xl hover:bg-travel-blue-50 transition-colors" data-id="element-415">
                    Print Confirmation
                  </button>
                  <button onClick={() => window.location.hash = ''} className="flex-1 py-3 bg-travel-blue-600 text-white font-semibold rounded-xl hover:bg-travel-blue-700 transition-colors" data-id="element-416">
                    Back to Home
                  </button>
                </div>
              </motion.div>}
          </div>

          {/* Booking Summary Sidebar */}
          <div className="lg:col-span-1" data-id="element-417">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-24" data-id="element-418">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-419">
                Booking Summary
              </h3>

              {/* Hotel Info */}
              <div className="mb-6" data-id="element-420">
                <img src={bookingData.hotelImage} alt={bookingData.hotelName} className="w-full h-40 object-cover rounded-lg mb-3" data-id="element-421" />
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-422">
                  {bookingData.hotelName}
                </h4>
                <p className="text-sm text-gray-600 flex items-center gap-1" data-id="element-423">
                  <MapPin className="w-3 h-3" data-id="element-424" />
                  {bookingData.hotelLocation}
                </p>
              </div>

              {/* Room Info */}
              <div className="border-t border-gray-200 pt-4 mb-4" data-id="element-425">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2" data-id="element-426">
                  <BedDouble className="w-4 h-4" data-id="element-427" />
                  <span className="font-medium" data-id="element-428">{bookingData.roomName}</span>
                </div>
                {checkIn && checkOut && <div className="flex items-center gap-2 text-sm text-gray-600 mb-2" data-id="element-429">
                    <Clock className="w-4 h-4" data-id="element-430" />
                    <span data-id="element-431">
                      {nights} {nights === 1 ? 'night' : 'nights'}
                    </span>
                  </div>}
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-432">
                  <Users className="w-4 h-4" data-id="element-433" />
                  <span data-id="element-434">
                    {guests} {guests === 1 ? 'guest' : 'guests'}
                  </span>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-gray-200 pt-4 space-y-3" data-id="element-435">
                <div className="flex justify-between text-sm" data-id="element-436">
                  <span className="text-gray-600" data-id="element-437">
                    ${bookingData.roomPrice} × {nights}{' '}
                    {nights === 1 ? 'night' : 'nights'}
                  </span>
                  <span className="font-medium text-gray-900" data-id="element-438">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-439">
                  <span className="text-gray-600" data-id="element-440">Taxes & Fees</span>
                  <span className="font-medium text-gray-900" data-id="element-441">
                    ${taxesAndFees.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between" data-id="element-442">
                  <span className="font-bold text-gray-900" data-id="element-443">Total</span>
                  <span className="font-bold text-2xl text-travel-blue-600" data-id="element-444">
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="border-t border-gray-200 pt-4 mt-4 space-y-2" data-id="element-445">
                <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-446">
                  <Check className="w-4 h-4 text-green-600" data-id="element-447" />
                  <span data-id="element-448">Free cancellation up to 48 hours</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-449">
                  <Check className="w-4 h-4 text-green-600" data-id="element-450" />
                  <span data-id="element-451">Best price guarantee</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-452">
                  <Check className="w-4 h-4 text-green-600" data-id="element-453" />
                  <span data-id="element-454">Instant confirmation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer data-id="element-455" />
    </div>;
}